/**
 * Widget API client for a Brain Agent's public channel (`/api/v1/brain-webchat`).
 *
 * Outputs are durable rows read from a cursor, so a reload catches up from the
 * record; only a reply still being written comes from the token buffer.
 */

import { brainFormPayload } from './brainEventParsers';
import { withCountryCodes } from '../countryData';
import { capEmbedParams, verificationBody, type WidgetVerification } from './embedParams';
import type { QuickAction } from '../types';
import { parseDisplayFormEvent, parseQuickActions } from './eventParsers';
import type { DisplayFormData, WebchatConfig } from './types';

const DEFAULT_API_BASE = 'https://api.app.pleiful.ai';

/**
 * One id per page load, sent as `X-Client-ID`. Plain-HTTP pages have no
 * `crypto.randomUUID`, hence the random-bytes fallback.
 */
const CLIENT_ID =
  typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : Array.from(crypto.getRandomValues(new Uint8Array(16)), (byte) => byte.toString(16).padStart(2, '0')).join('');

/** One restored message, with the suggestions drawn under it if any. */
export interface HistoryEntry {
  role: string;
  content: string;
  failed?: boolean;
  quick_actions?: QuickAction[];
  /** A form the visitor sent at this point: restored as sent, never drawn. */
  submitted_form_id?: string;
}

/** One durable output row, as the outputs route returns it. */
export interface BrainOutput {
  sequence: number;
  kind: string;
  output_id: string;
  payload: Record<string, unknown>;
}

/** The channel config, as the config route returns it. */
export interface BrainWebchatConfigBody {
  resolved_language?: string;
  greeting_text?: string;
  how_can_we_help_text?: string | null;
  thinking_text?: string | null;
  header_title?: string | null;
  suggestions?: Array<{
    label: string;
    search_query?: string | null;
    component_id?: string | null;
    bg_color?: string | null;
    text_color?: string | null;
    open_chat?: boolean;
  }>;
  message_templates?: Array<{
    label: string;
    message?: string | null;
    emoji?: string | null;
    component_id?: string | null;
  }>;
  top_button?: {
    label: string;
    component_id: string;
    emoji?: string | null;
    description?: string | null;
  } | null;
  top_button_font_size?: number | null;
  bubble_size?: string;
  rounded_buttons?: boolean;
  use_gradient?: boolean;
  primary_hover_color?: string | null;
  open_links_in_new_tab?: boolean;
  carousel_image_aspect?: string | null;
  ai_avatar_url?: string | null;
  user_avatar_url?: string | null;
  hover_arrow_url?: string | null;
  expand_icon_url?: string | null;
  collapse_icon_url?: string | null;
  card_bg_color?: string | null;
  card_text_color?: string | null;
  card_badge_bg_color?: string | null;
  quick_action_bg_color?: string | null;
  quick_action_text_color?: string | null;
  quick_action_font_size?: number | null;
  regular_text_size?: number | null;
  special_text_size?: number | null;
  form_text_size?: number | null;
  form_button_font_size?: number | null;
  quick_actions_enabled?: boolean;
  layout_variant?: string;
  default_position?: string;
  bottom_offset_percent?: number | null;
  default_width?: number | null;
  default_height?: number | null;
  logo_url?: string | null;
  icon_url?: string | null;
  primary_color?: string | null;
  user_message_bg_color?: string | null;
  user_message_text_color?: string | null;
  assistant_message_bg_color?: string | null;
  assistant_message_text_color?: string | null;
  form_bg_color?: string | null;
  form_text_color?: string | null;
  font_family?: string | null;
  secondary_font_family?: string | null;
  param_fields?: Array<{ key: string; source: string; cookie_name?: string | null }>;
}

export class BrainWebchatApiClient {
  private apiKey: string;
  private baseUrl: string;
  private lang: string | undefined;
  /** What the host page knows about its visitor: snippet params, folded cookies. */
  private params: Record<string, unknown> | undefined;
  /** The host's HMAC over the verified params, if the page carries one. */
  private verification: WidgetVerification | undefined;
  /**
   * Whether the Session has not taken the bag yet. Only a change re-sends it,
   * since the backend accepts a signature once; owed from the start when the
   * snippet carries anything, as a restored Session is never opened.
   */
  private paramsDirty: boolean;
  /** Bumped by every change to the bag, so a request only settles what it sent. */
  private paramsVersion = 0;
  /** One `/stream` read, held from the status call until the chunks are asked for. */
  private lastStream: { sessionId: string; streamed: BrainStreamBody } | null = null;
  /** The last sequence each restored Session's history was read through. */
  private restored = new Map<string, number>();

  constructor(
    apiKey: string,
    baseUrl?: string,
    lang?: string,
    params?: Record<string, unknown>,
    verification?: WidgetVerification,
  ) {
    this.apiKey = apiKey;
    this.baseUrl = baseUrl || DEFAULT_API_BASE;
    this.lang = lang;
    this.params = capEmbedParams(params);
    this.verification = verification;
    this.paramsDirty = !!this.params || !!verification;
  }

  /** Merge into what the page knows (and set the signature); the next request carries it. */
  setParams(params?: Record<string, unknown>, verification?: WidgetVerification): void {
    this.params = capEmbedParams({ ...this.params, ...params });
    this.verification = verification;
    this.markOwed();
  }

  /**
   * Fold in cookie params beneath what the bag holds, leaving the signature: a
   * host's `setParams` may have landed first.
   */
  foldParams(params: Record<string, unknown>): void {
    this.params = capEmbedParams({ ...params, ...this.params });
    this.markOwed();
  }

  private markOwed(): void {
    this.paramsDirty = true;
    this.paramsVersion++;
  }

  /** Settle what a request carried, unless the bag changed while it was in flight. */
  private settle(sentVersion: number): void {
    if (this.paramsVersion === sentVersion) this.paramsDirty = false;
  }

  /** The bag and signature for a body, when they are owed; empty otherwise. */
  private embedBody(owed: boolean): Record<string, unknown> {
    if (!owed || (!this.params && !this.verification)) return {};
    return { params: this.params ?? null, verification: verificationBody(this.verification) };
  }

  /** The key, which the remembered conversation is stored under. */
  getApiKey(): string {
    return this.apiKey;
  }

  getBaseUrl(): string {
    return this.baseUrl;
  }

  /** Follow a Session live (`sessionFollow`), from a cursor. */
  openFollow(sessionId: string, afterSequence: number | undefined, signal: AbortSignal): Promise<Response> {
    const query = afterSequence === undefined ? '' : `?after_sequence=${afterSequence}`;
    return fetch(`${this.baseUrl}/api/v1/brain-webchat/sessions/${encodeURIComponent(sessionId)}/live${query}`, {
      signal,
      headers: { 'X-Webchat-Api-Key': this.apiKey, 'X-Client-ID': CLIENT_ID, Accept: 'text/event-stream' },
    });
  }

  restoredThrough(sessionId: string): number | undefined {
    return this.restored.get(sessionId);
  }

  private get headers(): HeadersInit {
    return {
      'X-Webchat-Api-Key': this.apiKey,
      'X-Client-ID': CLIENT_ID,
      'Content-Type': 'application/json',
    };
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const response = await fetch(`${this.baseUrl}/api/v1/brain-webchat${path}`, {
      ...init,
      // Last, not first: the key is what every one of these routes is
      // authenticated by, so it is not something a caller may spread away.
      headers: this.headers,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw Object.assign(new Error(error.message || error.detail || `HTTP ${response.status}`), {
        code: typeof error.code === 'string' ? error.code : null,
      });
    }
    return response.json() as Promise<T>;
  }

  /**
   * Fetch the channel's config, widened to the shape the widget draws from.
   * The chrome follows the language the channel resolved, not the page's
   * `<html lang>`, so a pinned channel language holds.
   */
  async fetchConfig(): Promise<WebchatConfig> {
    const query = this.lang ? `?lang=${encodeURIComponent(this.lang)}` : '';
    return widgetConfigFromChannel(await this.request<BrainWebchatConfigBody>(`/config${query}`));
  }

  /** Open a Session, carrying what the page knows for the first turn's Process. */
  async createConversation(): Promise<string> {
    const sentVersion = this.paramsVersion;
    const embed = this.embedBody(true);
    const data = await this.request<{ id: string }>('/sessions', {
      method: 'POST',
      ...(Object.keys(embed).length > 0 ? { body: JSON.stringify(embed) } : {}),
    });
    // The Session holds the bag now; only a later change owes it again.
    this.settle(sentVersion);
    return data.id;
  }

  /** Post a message; answers once queued, and the reply arrives on the follow. */
  async sendMessage(
    sessionId: string,
    content: string,
  ): Promise<{ status: string; conversation_id: string }> {
    const owed = this.paramsDirty;
    const sentVersion = this.paramsVersion;
    await this.request(`/sessions/${sessionId}/events`, {
      method: 'POST',
      body: JSON.stringify({ event: { kind: 'message', text: content }, ...this.embedBody(owed) }),
    });
    // Only once the server took it: a refused turn owes it again.
    if (owed) this.settle(sentVersion);
    return { status: 'accepted', conversation_id: sessionId };
  }

  /**
   * Submit a form the Agent rendered; `formId` is the rendered component's id.
   *
   * Cookies go unfiltered as `params` and are filtered server-side: a widget
   * filtering its own submission would be a page vouching for itself.
   */
  async submitForm(
    sessionId: string,
    componentId: string,
    values: Record<string, unknown>,
    _context?: Record<string, unknown>,
    params?: Record<string, unknown>,
  ): Promise<{ status: string; conversation_id: string }> {
    const owed = this.paramsDirty;
    const sentVersion = this.paramsVersion;
    await this.request(`/sessions/${sessionId}/events`, {
      method: 'POST',
      body: JSON.stringify({
        event: {
          kind: 'form_submit',
          component_id: componentId,
          values: withCountryCodes(values),
          ...(params && Object.keys(params).length > 0 ? { params } : {}),
        },
        ...this.embedBody(owed),
      }),
    });
    if (owed) this.settle(sentVersion);
    return { status: 'accepted', conversation_id: sessionId };
  }

  /**
   * Whether a reply is being written right now. Status and chunks come from one
   * held `/stream` read, so they never describe different moments.
   */
  async getStreamStatus(
    sessionId: string,
  ): Promise<{ active: boolean; turn_event_id?: string | null; next_index?: number }> {
    const streamed = await this.request<BrainStreamBody>(`/sessions/${sessionId}/stream`);
    this.lastStream = { sessionId, streamed };
    return {
      active: streamed.active,
      turn_event_id: streamed.turn_event_id,
      next_index: streamed.next_index,
    };
  }

  /**
   * The reply so far, as the one `token` the widget's replay appends. `total`
   * is the fragment count a live frame's `index` is measured against.
   */
  async getStreamChunks(
    sessionId: string,
  ): Promise<{ chunks: Array<{ event: string; data: Record<string, unknown> }>; total: number; status: string }> {
    const held = this.lastStream?.sessionId === sessionId ? this.lastStream.streamed : null;
    const streamed = held ?? (await this.request<BrainStreamBody>(`/sessions/${sessionId}/stream`));
    this.lastStream = null;
    return {
      chunks: streamed.text ? [{ event: 'token', data: { content: streamed.text } }] : [],
      total: streamed.next_index,
      status: streamed.active ? 'streaming' : 'complete',
    };
  }

  /** Rebuild the conversation after a reload, from the durable record. */
  async getMessageHistory(
    sessionId: string,
  ): Promise<HistoryEntry[]> {
    return readHistory(
      (cursor) =>
        this.request<BrainOutputPage>(
          `/sessions/${sessionId}/outputs${cursor === null ? '' : `?after_sequence=${cursor}`}`,
        ),
      (sequence) => this.restored.set(sessionId, sequence),
    );
  }

  /**
   * Draw a component the visitor opened by name (a carousel button), inside
   * their conversation. Needs a Session: the route authorizes against the
   * Agent version it opened on.
   */
  async fetchForm(formId: string, conversationId?: string | null): Promise<DisplayFormData> {
    if (!conversationId) {
      throw new Error(
        `KapiaWidget: a Brain form is opened inside a conversation; there is none to open ${formId} in`,
      );
    }
    const query = this.lang ? `?lang=${encodeURIComponent(this.lang)}` : '';
    const drawn = await this.request<BrainFormRenderBody>(
      `/sessions/${encodeURIComponent(conversationId)}/components/${encodeURIComponent(formId)}${query}`,
    );
    return parseDisplayFormEvent(brainFormPayload(drawn as unknown as Record<string, unknown>));
  }
}

/** What `GET /sessions/{id}/stream` answers. */
interface BrainStreamBody {
  active: boolean;
  turn_event_id: string | null;
  text: string;
  next_index: number;
}

/** One `display_form` render event, as the component route answers with it. */
export interface BrainFormRenderBody {
  type: string;
  render_event_id: string;
  component_id: string;
  title: string;
  description?: string | null;
  fields: Array<Record<string, unknown>>;
  start_open?: boolean;
  allow_multiple_submissions?: boolean;
}

/** One page of a Session's outputs. */
export interface BrainOutputPage {
  outputs: BrainOutput[];
  next_cursor?: number | null;
}

/**
 * A channel's config, widened to the shape the widget draws from. What the
 * channel does not author is left absent, the widget's "use the default".
 */
export function widgetConfigFromChannel(data: BrainWebchatConfigBody): WebchatConfig {
  return {
    // The channel's own name for the window, not the Application's: see
    // `header_title` on the channel config.
    agentName: data.header_title || 'Assistant',
    resolvedLanguage: data.resolved_language ?? null,
    greetingText: data.greeting_text || 'Hello!',
    // A channel may have no help line: empty draws no line under the greeting
    // and leaves the message box its own placeholder.
    howCanWeHelpText: data.how_can_we_help_text ?? '',
    thinkingText: data.thinking_text ?? null,
    // A template with no message sends its label, as the channel stores it.
    messageTemplates: (data.message_templates || []).map((t) => ({
      label: t.label,
      message: t.message || t.label,
      icon: t.emoji ?? undefined,
      // The widget's form plumbing opens the component, since the Brain
      // client's `fetchForm` is the component route.
      formId: t.component_id ?? undefined,
    })),
    suggestions: (data.suggestions || []).map((s) => ({
      label: s.label,
      searchQuery: s.search_query ?? undefined,
      formId: s.component_id ?? undefined,
      openChat: s.open_chat ?? undefined,
      // Absent rather than null, so a bubble that authored no colour keeps the
      // widget's own.
      bgColor: s.bg_color ?? undefined,
      textColor: s.text_color ?? undefined,
    })),
    paramFields: (data.param_fields || []).map((f) => ({
      key: f.key,
      source: f.source === 'cookie' ? ('cookie' as const) : ('snippet' as const),
      cookieName: f.cookie_name ?? null,
    })),
    bubbleSize: data.bubble_size === 'small' ? 'small' : 'medium',
    layoutVariant: data.layout_variant === 'searchbar' ? 'searchbar' : 'floating',
    defaultPosition: data.default_position === 'left' ? 'left' : 'right',
    bottomOffsetPercent: data.bottom_offset_percent ?? null,
    carouselImageAspect: data.carousel_image_aspect ?? null,
    logoUrl: data.logo_url ?? null,
    iconUrl: data.icon_url ?? null,
    aiAvatarUrl: data.ai_avatar_url ?? null,
    userAvatarUrl: data.user_avatar_url ?? null,
    hoverArrowUrl: data.hover_arrow_url ?? null,
    expandIconUrl: data.expand_icon_url ?? null,
    collapseIconUrl: data.collapse_icon_url ?? null,
    primaryColor: data.primary_color ?? null,
    primaryHoverColor: data.primary_hover_color ?? null,
    useGradient: data.use_gradient ?? false,
    userMessageBgColor: data.user_message_bg_color ?? null,
    userMessageTextColor: data.user_message_text_color ?? null,
    assistantMessageBgColor: data.assistant_message_bg_color ?? null,
    assistantMessageTextColor: data.assistant_message_text_color ?? null,
    cardBgColor: data.card_bg_color ?? null,
    cardTextColor: data.card_text_color ?? null,
    cardBadgeBgColor: data.card_badge_bg_color ?? null,
    quickActionBgColor: data.quick_action_bg_color ?? null,
    quickActionTextColor: data.quick_action_text_color ?? null,
    formBgColor: data.form_bg_color ?? null,
    formTextColor: data.form_text_color ?? null,
    fontFamily: data.font_family ?? null,
    secondaryFontFamily: data.secondary_font_family ?? null,
    regularTextSize: data.regular_text_size ?? null,
    specialTextSize: data.special_text_size ?? null,
    quickActionFontSize: data.quick_action_font_size ?? null,
    formTextSize: data.form_text_size ?? null,
    formButtonFontSize: data.form_button_font_size ?? null,
    topButtonFontSize: data.top_button_font_size ?? null,
    // Absent from an older server; `true` is the widget's default.
    roundedButtons: data.rounded_buttons ?? true,
    // Absent from an older server, where nothing writes them.
    quickActionsEnabled: data.quick_actions_enabled ?? true,
    defaultWidth: data.default_width ?? null,
    defaultHeight: data.default_height ?? null,
    topButton: data.top_button
      ? {
          label: data.top_button.label,
          formId: data.top_button.component_id,
          emoji: data.top_button.emoji ?? undefined,
          description: data.top_button.description ?? undefined,
        }
      : null,
    openLinksInNewTab: data.open_links_in_new_tab ?? false,
  };
}

/**
 * A Session's conversation rebuilt from its durable record, for a reload.
 *
 * Paged, or a long conversation would restore only its oldest rows. A sent
 * non-`repeatable` form is kept so a reload cannot re-enable it and send the
 * same lead twice.
 */
export async function readHistory(
  fetchPage: (afterSequence: number | null) => Promise<BrainOutputPage>,
  onReadThrough?: (sequence: number) => void,
): Promise<HistoryEntry[]> {
  const rows: BrainOutput[] = [];
  let cursor: number | null = null;
  for (;;) {
    const page = await fetchPage(cursor);
    rows.push(...page.outputs);
    // Anything but a number ends it, or a missing cursor would loop forever.
    if (typeof page.next_cursor !== 'number' || page.outputs.length === 0) break;
    cursor = page.next_cursor;
  }
  const last = rows[rows.length - 1];
  if (last) onReadThrough?.(last.sequence);
  const history: HistoryEntry[] = [];
  for (const output of rows) {
    if (output.kind === 'form_submitted') {
      const formId = output.payload.component_id;
      if (typeof formId === 'string' && output.payload.repeatable !== true) {
        history.push({ role: 'user', content: '', submitted_form_id: formId });
      }
      continue;
    }
    if (output.kind === 'turn_failed') {
      history.push({ role: 'assistant', content: '', failed: true });
      continue;
    }
    // Suggestions follow the reply they were written for, which is the last
    // assistant entry so far: they are recorded after the turn that produced it.
    if (output.kind === 'quick_actions') {
      const reply = history[history.length - 1];
      if (reply?.role === 'assistant') reply.quick_actions = parseQuickActions(output.payload);
      continue;
    }
    if (output.kind !== 'message' && output.kind !== 'user_message') continue;
    const content = (output.payload.text as string) || '';
    if (!content) continue;
    history.push({ role: output.kind === 'user_message' ? 'user' : 'assistant', content });
  }
  return history;
}
